// In dev, each frontend app runs on its own Vite port. Cross-app navigations
// like `/dashboard` → http://127.0.0.1:6004/ must be rewritten so
// the browser switches port. In production Flask serves all apps under one
// origin, so the bare path works as-is and this helper is a no-op.
//
// Because each port is a distinct origin, localStorage is NOT shared between
// apps in dev. We work around it by appending the auth tokens to the URL
// fragment when navigating across apps, and consuming + stripping the fragment
// on the receiving end (consumeAuthHash, called from each app's main entry).

// Each Vite dev server has a clean-url rewrite middleware so dev URLs match
// the production surface (only the port differs). See cleanUrlRewrite in each
// vite.config.ts.
const DEV_MAP: Record<string, string> = {
  '/dashboard':      'http://127.0.0.1:6004/',
  '/sinvosly':       'http://127.0.0.1:6003/',
  '/login':          'http://127.0.0.1:6004/login',
  '/signup':         'http://127.0.0.1:6004/signup',
  '/admin-login':    'http://127.0.0.1:6003/admin-login',
  '/sinvosly/login': 'http://127.0.0.1:6003/admin-login',
  '/':               'http://127.0.0.1:6001/',
  '/product':        'http://127.0.0.1:6001/product',
  '/m':              'http://127.0.0.1:6001/m',
};

const AUTH_KEYS = ['invosly_token', 'invosly_refresh', 'invosly_user', 'invosly_org'] as const;
const HASH_PARAM = '__auth';

// Only navigations into authenticated surfaces need the token hash. Marketing,
// public, and the auth pages themselves do not — adding it there just clutters
// the URL bar (and gets bookmarked).
const AUTH_REQUIRED_PATH_PREFIXES = ['/dashboard', '/sinvosly'];

function pathRequiresAuth(path: string): boolean {
  // /sinvosly/login is the admin login page (auth surface), NOT the auth-gated SPA.
  if (path === '/sinvosly/login' || path.startsWith('/sinvosly/login/') ||
      path.startsWith('/sinvosly/login?') || path.startsWith('/sinvosly/login#')) return false;
  return AUTH_REQUIRED_PATH_PREFIXES.some(p =>
    path === p || path.startsWith(p + '/') || path.startsWith(p + '?') || path.startsWith(p + '#'),
  );
}

function isDev(): boolean {
  return (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
}

function resolveDevUrl(path: string): string {
  if (DEV_MAP[path]) return DEV_MAP[path];
  for (const [k, v] of Object.entries(DEV_MAP)) {
    if (path.startsWith(k + '/') || path.startsWith(k + '?') || path.startsWith(k + '#')) {
      return v + path.slice(k.length);
    }
  }
  return path;
}

function packAuthHash(): string {
  try {
    const payload: Record<string, string> = {};
    for (const k of AUTH_KEYS) {
      const v = localStorage.getItem(k);
      if (v != null) payload[k] = v;
    }
    if (Object.keys(payload).length === 0) return '';
    return HASH_PARAM + '=' + encodeURIComponent(JSON.stringify(payload));
  } catch { return ''; }
}

export function crossAppHref(path: string): string {
  if (!isDev()) return path;
  const url = resolveDevUrl(path);
  if (url === path) return url;  // not a cross-app link, no port hop happening
  if (!pathRequiresAuth(path)) return url;
  const blob = packAuthHash();
  if (!blob) return url;
  return url + (url.includes('#') ? '&' : '#') + blob;
}

// Call once at app boot (top of main.tsx), before any code reads getToken().
// If URL hash carries an `__auth=...` blob, hydrate localStorage from it and
// strip the fragment so it doesn't end up in browser history / bookmarks.
export function consumeAuthHash(): void {
  if (!isDev()) return;
  try {
    const h = window.location.hash;
    if (!h || h.indexOf(HASH_PARAM + '=') < 0) return;
    const m = h.match(new RegExp('[#&]' + HASH_PARAM + '=([^&]+)'));
    if (!m) return;
    const payload = JSON.parse(decodeURIComponent(m[1])) as Record<string, string>;
    for (const k of AUTH_KEYS) {
      if (typeof payload[k] === 'string') localStorage.setItem(k, payload[k]);
    }
    // Strip the fragment from the URL bar without reloading.
    const cleanHash = h.replace(new RegExp('[#&]?' + HASH_PARAM + '=[^&]+'), '');
    history.replaceState(null, '', window.location.pathname + window.location.search + cleanHash);
  } catch { /* ignore — fall through to normal no-token redirect */ }
}
